import { createContext } from "react";
import type { ColorPalette } from "../types/narrative";

/**
 * Where a `==highlight==` is in its life, decided by the group that contains it:
 *
 * - `"pending"` — the group is still revealing its items; hold the band collapsed.
 * - `"play"`    — every item has finished; sweep after the start delay.
 * - `"static"`  — draw it finished, no sweep. Backward navigation, reduced
 *   motion, `animationStyle: "none"` groups and PDF export all skip the item
 *   reveal, so the highlight skips its own sweep with them. It is also the
 *   default for text rendered outside any group.
 */
export type HighlightPhase = "pending" | "play" | "static";

export interface HighlightContextType {
  phase: HighlightPhase;
  /**
   * The section's effective palette: the backdrop the contrast guard checks an
   * authored highlight colour against.
   */
  palette?: ColorPalette;
}

export const HighlightContext = createContext<HighlightContextType>({
  phase: "static",
});

/**
 * Phase for a group's highlights. `currentItemIndex` runs from -1 (not started)
 * up to `itemCount` (every item has fired onComplete), mirroring InsightGroup's
 * own "all items done" check.
 */
export function getHighlightPhase({
  skipSequentialReveal,
  currentItemIndex,
  itemCount,
}: {
  skipSequentialReveal: boolean;
  currentItemIndex: number;
  itemCount: number;
}): HighlightPhase {
  if (skipSequentialReveal) return "static";
  return currentItemIndex >= itemCount ? "play" : "pending";
}
